export const getDateRangeBounds = (startDate, endDate) => {
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;
  if (start) start.setHours(0, 0, 0, 0);
  if (end) end.setHours(23, 59, 59, 999);
  return { start, end };
};

export const filterRowsByDateRange = (
  rows = [],
  startDate = "",
  endDate = "",
  getRowDate = (row) => row?.date
) => {
  const { start, end } = getDateRangeBounds(startDate, endDate);
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const d = new Date(getRowDate(row));
    if (start && d < start) return false;
    if (end && d > end) return false;
    return true;
  });
};

// A utility record can be saved later than the date on which it should affect
// the statement. Always prefer that transaction date; use saved timestamps only
// for older records that do not have one.
export const getUtilityStatementDate = (row = {}) =>
  row?.date || row?.sortTimestamp || row?.timestamp || null;

export const getPaymentSplit = (payment) => {
  const amount = Number(payment?.amount || 0);
  const split = payment?.split || {};
  const platform = Number(split.platformAmount ?? 0);
  const manager = Number(split.managerAmount ?? 0);
  const landlord =
    split.landlordAmount == null
      ? amount - platform - manager
      : Number(split.landlordAmount || 0);
  return { amount, platform, manager, landlord };
};

export const sumPaymentRows = (rows = []) =>
  (Array.isArray(rows) ? rows : []).reduce(
    (acc, row) => {
      const split = getPaymentSplit(row);
      acc.amount += split.amount;
      acc.platform += split.platform;
      acc.manager += split.manager;
      acc.landlord += split.landlord;
      return acc;
    },
    { amount: 0, platform: 0, manager: 0, landlord: 0 }
  );

// Walks an already-filtered list of utility statement rows and stamps a
// `runningBalance` on each, seeded from the statement's outstanding amount.
// Extracted verbatim from BillsView + FRPModule so both share one implementation
// and it can be unit-tested. IMPORTANT: it must receive rows that are ALREADY
// filtered by the caller (the running balance is intentionally computed over the
// currently-shown slice). Wallet and water-meter events do not change this debt
// balance, even when an older API response includes a wallet balance snapshot on
// the row. Rows are consumed in incoming (display) order.
export const computeUtilityStatementRunningBalances = (filteredRows = [], seedOutstandingAmount = 0) => {
  let runningBalance = Number(seedOutstandingAmount || 0);

  return (Array.isArray(filteredRows) ? filteredRows : []).map((row) => {
    const hasStoredBalanceAfter =
      row?.balanceAfter !== null &&
      row?.balanceAfter !== undefined &&
      Number.isFinite(Number(row.balanceAfter));
    const hasStoredBalanceBefore =
      row?.balanceBefore !== null &&
      row?.balanceBefore !== undefined &&
      Number.isFinite(Number(row.balanceBefore));
    const usesWalletBalance = row?.balanceScope === "utility_account_wallet";
    // Water-meter rows (top-ups / reading updates) are meter+wallet events, not
    // debt movements: buying or being gifted water units never settles an existing
    // debt invoice (a waiver's debt effect is its own waiver row). Their
    // paymentAmount must NOT advance the debt accumulator — counting it made every
    // older row's balance climb by the top-up amount (the "waived 100 and the
    // 300 became 700" bug). They display the carried debt balance instead.
    const rowType = String(row?.type || "").trim().toLowerCase();
    const isMeterEvent =
      row?.source === "water_meter" || rowType === "water_top_up" || rowType === "reading_update";
    const isNonDebtEvent = usesWalletBalance || isMeterEvent;
    const rowRunningBalance = !isNonDebtEvent && hasStoredBalanceAfter
      ? Number(row.balanceAfter)
      : runningBalance;
    const rowWithRunningBalance = {
      ...row,
      runningBalance: rowRunningBalance,
    };

    if (!isNonDebtEvent) {
      runningBalance = hasStoredBalanceBefore
        ? Number(row.balanceBefore)
        : Number(
            (rowRunningBalance - Number(row.chargeAmount || 0) + Number(row.paymentAmount || 0)).toFixed(2)
          );
    }

    return rowWithRunningBalance;
  });
};

export const sumCurrentUnitBalances = (units = [], managerUnitBalances = {}) =>
  (Array.isArray(units) ? units : []).reduce((sum, unit) => {
    let balance = managerUnitBalances?.[unit?._id];
    if (typeof balance !== "number") {
      const raw = unit?.balance;
      balance = typeof raw === "number" ? raw : parseFloat(raw) || 0;
    }
    return sum + (balance || 0);
  }, 0);

const NAIROBI_OFFSET_MINUTES = 3 * 60;

const toNairobiDate = (input) => {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  const utcMs = d.getTime() + d.getTimezoneOffset() * 60000;
  return new Date(utcMs + NAIROBI_OFFSET_MINUTES * 60000);
};

const pad2 = (value) => String(value).padStart(2, "0");

const monthLabelFromKey = (monthKey) => {
  const [yearPart, monthPart] = String(monthKey || "").split("-");
  const year = Number(yearPart);
  const month = Number(monthPart);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return monthKey || "";
  }
  const date = new Date(Date.UTC(year, month - 1, 1));
  return date.toLocaleString("en-US", { month: "short", year: "numeric" });
};

const roundMoney = (value) => Math.round(Number(value || 0) * 100) / 100;
const parseMonthKeyToTs = (monthKey) => {
  const [yearPart, monthPart] = String(monthKey || "").split("-");
  const year = Number(yearPart);
  const month = Number(monthPart);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return 0;
  }
  return Date.UTC(year, month - 1, 1);
};

const buildInvoicedAtLabel = (monthKeySet) => {
  const keys = Array.from(monthKeySet || []);
  if (!keys.length) return "-";
  const sorted = keys.sort((a, b) => parseMonthKeyToTs(a) - parseMonthKeyToTs(b));
  const labels = sorted.map((key) => monthLabelFromKey(key));
  if (labels.length === 1) return labels[0];
  return `${labels[0]} +${labels.length - 1} more`;
};

const dayKeyFromDate = (input) => {
  const d = toNairobiDate(input);
  if (!d) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const dayLabelFromKey = (dayKey) => {
  const [yearPart, monthPart, dayPart] = String(dayKey || "").split("-");
  const year = Number(yearPart);
  const month = Number(monthPart);
  const day = Number(dayPart);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return dayKey || "-";
  }
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
};

const classifyInvoiceKind = (description = "") => {
  const normalized = String(description || "").trim().toLowerCase();
  if (normalized.includes("late fee")) return "late_fee";
  if (normalized.includes("deposit")) return "deposit";
  if (normalized.includes("rent")) return "rent";
  if (normalized.includes("waiver")) return "waiver";
  return "other";
};

const buildInvoiceGroupsByDate = (transactions = []) => {
  const rows = Array.isArray(transactions) ? transactions : [];
  const dateGroups = new Map();

  rows.forEach((tx, index) => {
    const amount = Number(tx?.invoiceAmount || 0);
    if (!Number.isFinite(amount) || amount === 0) return;

    const dayKey = dayKeyFromDate(tx?.date);
    const groupKey = dayKey || `unknown-${index}`;
    const daySortTs = dayKey ? new Date(`${dayKey}T00:00:00.000Z`).getTime() : 0;

    if (!dateGroups.has(groupKey)) {
      dateGroups.set(groupKey, {
        dateKey: groupKey,
        dateLabel: dayKey ? dayLabelFromKey(dayKey) : "Unknown date",
        daySortTs,
        invoices: [],
        totalAmount: 0,
        firstIndex: index,
      });
    }

    const group = dateGroups.get(groupKey);
    group.totalAmount += amount;
    group.firstIndex = Math.min(group.firstIndex, index);
    group.invoices.push({
      invoiceId: tx?.invoiceId || null,
      description: tx?.description || "Invoice",
      amount: roundMoney(amount),
      status: tx?.status || null,
      kind: classifyInvoiceKind(tx?.description || ""),
    });
  });

  return Array.from(dateGroups.values())
    .sort((a, b) => {
      if (a.daySortTs !== b.daySortTs) return b.daySortTs - a.daySortTs;
      return a.firstIndex - b.firstIndex;
    })
    .map(({ daySortTs, firstIndex, ...group }) => ({
      ...group,
      totalAmount: roundMoney(group.totalAmount),
    }));
};

export const buildTenantMonthlyInvoiceSummary = (statement) => {
  const txs = Array.isArray(statement?.transactions) ? statement.transactions : [];
  const chronological = [...txs]
    .map((tx, index) => ({ tx, index }))
    .sort((a, b) => {
      const aTime = new Date(a.tx?.date || 0).getTime();
      const bTime = new Date(b.tx?.date || 0).getTime();
      if (aTime !== bTime) return aTime - bTime;

      // On same timestamp, process invoice debits before payments so
      // auto-assignment invoice+payment pairs settle correctly.
      const aIsInvoiceDebit = Number(a.tx?.invoiceAmount || 0) > 0 ? 1 : 0;
      const bIsInvoiceDebit = Number(b.tx?.invoiceAmount || 0) > 0 ? 1 : 0;
      if (aIsInvoiceDebit !== bIsInvoiceDebit) {
        return bIsInvoiceDebit - aIsInvoiceDebit;
      }

      return a.index - b.index;
    })
    .map((entry) => entry.tx);
  const invoiceGroups = buildInvoiceGroupsByDate(chronological);
  const monthBuckets = new Map();
  const paidTransactions = [];
  const invoiceQueue = [];
  const invoiceRows = [];
  const invoicePaymentsByInvoiceId = new Map();
  let unappliedCredit = 0;
  let runningBalance = 0;

  const ensureBucket = (monthKey, monthSortTs) => {
    if (!monthBuckets.has(monthKey)) {
      monthBuckets.set(monthKey, {
        monthKey,
        monthLabel: monthLabelFromKey(monthKey),
        invoiceAmount: 0,
        paymentApplied: 0,
        outstandingAmount: 0,
        invoiceCount: 0,
        clearedDate: null,
        monthSortTs,
      });
    }
    return monthBuckets.get(monthKey);
  };

  const applyCreditToOldest = (
    creditAmount,
    sourceDate = null,
    touchedMonthKeys = null,
    source = null
  ) => {
    let remaining = Number(creditAmount || 0);
    if (!Number.isFinite(remaining) || remaining <= 0) return 0;

    for (const openInvoice of invoiceQueue) {
      if (remaining <= 0) break;
      if (!openInvoice || openInvoice.remaining <= 0) continue;

      const apply = Math.min(openInvoice.remaining, remaining);
      openInvoice.remaining -= apply;
      remaining -= apply;

      if (openInvoice.invoiceRow) {
        openInvoice.invoiceRow.paidAmount += apply;
        openInvoice.invoiceRow.balance -= apply;
        if (openInvoice.invoiceRow.balance <= 0 && !openInvoice.invoiceRow.clearedDate) {
          openInvoice.invoiceRow.clearedDate = sourceDate;
        }

        const targetInvoiceId = openInvoice.invoiceRow.invoiceId;
        if (targetInvoiceId && source && Number.isFinite(apply) && apply > 0) {
          const key = String(targetInvoiceId);
          if (!invoicePaymentsByInvoiceId.has(key)) invoicePaymentsByInvoiceId.set(key, []);
          invoicePaymentsByInvoiceId.get(key).push({
            date: source?.date || sourceDate || null,
            type: source?.type || "payment",
            description: source?.description || "",
            paymentId: source?.paymentId || null,
            amountApplied: roundMoney(apply),
          });
        }
      }

      const bucket = monthBuckets.get(openInvoice.monthKey);
      if (bucket) {
        if (touchedMonthKeys && typeof touchedMonthKeys.add === "function") {
          touchedMonthKeys.add(openInvoice.monthKey);
        }
        bucket.paymentApplied += apply;
        bucket.outstandingAmount -= apply;
        if (bucket.outstandingAmount <= 0 && !bucket.clearedDate) {
          bucket.clearedDate = sourceDate;
        }
      }
    }
    return remaining;
  };

  for (let idx = 0; idx < chronological.length; idx += 1) {
    const tx = chronological[idx];
    const invoiceAmount = Number(tx?.invoiceAmount || 0);
    const paymentAmount = Number(tx?.paymentAmount || 0);
    const nairobiDate = toNairobiDate(tx?.date);
    const monthKey = nairobiDate
      ? `${nairobiDate.getFullYear()}-${pad2(nairobiDate.getMonth() + 1)}`
      : "unknown";
    const monthSortTs = nairobiDate
      ? new Date(nairobiDate.getFullYear(), nairobiDate.getMonth(), 1).getTime()
      : 0;

    if (invoiceAmount > 0) {
      const bucket = ensureBucket(monthKey, monthSortTs);
      bucket.invoiceAmount += invoiceAmount;
      bucket.outstandingAmount += invoiceAmount;
      bucket.invoiceCount += 1;
      const invoiceRow = {
        invoiceId: tx?.invoiceId || null,
        date: tx?.date || null,
        description: tx?.description || "Invoice",
        kind: classifyInvoiceKind(tx?.description || ""),
        invoiceAmount,
        paidAmount: 0,
        balance: invoiceAmount,
        clearedDate: null,
        sourceIndex: idx,
      };
      invoiceRows.push(invoiceRow);
      invoiceQueue.push({ monthKey, remaining: invoiceAmount, invoiceRow });
      runningBalance += invoiceAmount;

      // If a prior waiver/payment created unapplied credit, automatically use it
      // against invoices that are created later (same as the running net balance).
      if (unappliedCredit > 0) {
        unappliedCredit = applyCreditToOldest(unappliedCredit, tx?.date || null, null, {
          type: "credit",
          date: tx?.date || null,
          description: "Credit applied",
        });
      }
    } else if (invoiceAmount < 0) {
      invoiceRows.push({
        invoiceId: tx?.invoiceId || null,
        date: tx?.date || null,
        description: tx?.description || "Invoice waiver",
        kind: classifyInvoiceKind(tx?.description || ""),
        invoiceAmount,
        paidAmount: 0,
        balance: 0,
        clearedDate: tx?.date || null,
        sourceIndex: idx,
      });
      const sourceAmount = Math.abs(invoiceAmount);
      const leftoverCredit = applyCreditToOldest(sourceAmount, tx?.date || null, null, {
        type: "waiver",
        date: tx?.date || null,
        description: tx?.description || "Invoice waiver",
      });
      if (leftoverCredit > 0) unappliedCredit += leftoverCredit;
      runningBalance -= sourceAmount;
    }

    if (paymentAmount > 0) {
      const touchedMonthKeys = new Set();
      const leftoverPayment = applyCreditToOldest(paymentAmount, tx?.date || null, touchedMonthKeys, {
        type: "payment",
        date: tx?.date || null,
        paymentId: tx?.paymentId || null,
        description: tx?.description || "Payment",
      });
      runningBalance -= paymentAmount;
      paidTransactions.push({
        date: tx?.date || null,
        invoicedAt: buildInvoicedAtLabel(touchedMonthKeys),
        amount: roundMoney(paymentAmount),
        totalBalance: roundMoney(runningBalance),
      });
      if (leftoverPayment > 0) unappliedCredit += leftoverPayment;
    }
  }

  const rows = Array.from(monthBuckets.values()).map((bucket) => {
    const invoiceAmount = roundMoney(bucket.invoiceAmount);
    const paymentApplied = roundMoney(bucket.paymentApplied);
    const outstandingAmount = roundMoney(bucket.outstandingAmount);
    const isUnpaid = outstandingAmount > 0;
    const status = isUnpaid
      ? paymentApplied > 0
        ? "Unpaid (Partial)"
        : "Unpaid"
      : "Paid";

    return {
      monthKey: bucket.monthKey,
      monthLabel: bucket.monthLabel,
      invoiceAmount,
      paymentApplied,
      outstandingAmount,
      invoiceCount: bucket.invoiceCount,
      clearedDate: bucket.clearedDate,
      status,
      isUnpaid,
      monthSortTs: bucket.monthSortTs,
    };
  });

  const unpaidRows = rows
    .filter((row) => row.isUnpaid)
    .sort((a, b) => b.monthSortTs - a.monthSortTs);

  const paidRows = rows
    .filter((row) => !row.isUnpaid)
    .sort((a, b) => b.monthSortTs - a.monthSortTs);

  const months = [...unpaidRows, ...paidRows].map(({ monthSortTs, ...row }) => row);
  const totalInvoiced = roundMoney(months.reduce((sum, row) => sum + row.invoiceAmount, 0));
  const totalApplied = roundMoney(months.reduce((sum, row) => sum + row.paymentApplied, 0));
  const totalOutstanding = roundMoney(months.reduce((sum, row) => sum + row.outstandingAmount, 0));
  const netBalance = roundMoney(runningBalance);
  const unpaidMonths = months.filter((row) => row.isUnpaid).length;
  const oldestUnpaid = [...rows]
    .filter((row) => row.isUnpaid)
    .sort((a, b) => a.monthSortTs - b.monthSortTs)[0]?.monthLabel || null;
  const paidTransactionsSorted = [...paidTransactions]
    .sort((a, b) => new Date(b?.date || 0) - new Date(a?.date || 0));
  const invoiceRowsSorted = [...invoiceRows]
    .map((row) => {
      const invoiceAmount = roundMoney(row.invoiceAmount);
      const paidAmount = roundMoney(row.paidAmount);
      const normalizedBalance = roundMoney(row.balance);
      return {
        ...row,
        invoiceAmount,
        paidAmount,
        balance: Math.abs(normalizedBalance) < 0.01 ? 0 : normalizedBalance,
      };
    })
    .sort((a, b) => {
      const aTime = new Date(a?.date || 0).getTime();
      const bTime = new Date(b?.date || 0).getTime();
      if (aTime !== bTime) return bTime - aTime;
      return (b?.sourceIndex || 0) - (a?.sourceIndex || 0);
    })
    .map(({ sourceIndex, ...row }) => row);

  const invoicePayments = Object.fromEntries(
    Array.from(invoicePaymentsByInvoiceId.entries()).map(([invoiceId, rows]) => [
      invoiceId,
      [...rows].sort((a, b) => new Date(a?.date || 0) - new Date(b?.date || 0)),
    ])
  );

  return {
    months,
    invoiceGroups,
    invoiceRows: invoiceRowsSorted,
    paidTransactions: paidTransactionsSorted,
    invoicePayments,
    summary: {
      totalInvoiced,
      totalApplied,
      totalOutstanding,
      netBalance,
      unpaidMonths,
      oldestUnpaid,
      unappliedCredit: roundMoney(unappliedCredit),
    },
  };
};
